import { formatDatetime } from 'app/shared/utils/date';
import { getRouteName, NOT_ALLOWED_BACK_ROUTES } from 'app/shared/utils/routes';
import { selectAgentOrgDisplayName } from 'app/modules/session/selectors';
import { selectRoutingHistory } from 'app/modules/navigator/selectors';
import { useHistory, useLocation } from 'react-router-dom';
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { IconArrowLeft, IconDownload } from '@u21/tabler-icons';
import {
  U21Button,
  U21ButtonProps,
  U21Page,
  U21PageProps,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { useReactToPrint } from 'react-to-print';
import { Unit21Logo } from 'app/shared/components/logos/Unit21Logo';

export interface U21DashboardPageProps extends U21PageProps {
  backButtonProps?: Omit<U21ButtonProps, 'as'>;
  backRoute: string;
  ignoredRoutes?: (route: string) => boolean;
  printProps?: { label: string };
}

const defaultIgnoredRoutes = () => false;

export const U21DashboardPage = (props: U21DashboardPageProps) => {
  const {
    action,
    backButtonProps,
    backRoute,
    beforeTitleContent,
    children,
    ignoredRoutes = defaultIgnoredRoutes,
    printProps,
    ...rest
  } = props;

  const { go, push } = useHistory();
  const { pathname, search } = useLocation();

  const orgName = useSelector(selectAgentOrgDisplayName);
  const routingHistory = useSelector(selectRoutingHistory);

  const { historyIndex, historyBackRoute } = useMemo(() => {
    const index = routingHistory.findIndex(
      (i) =>
        !NOT_ALLOWED_BACK_ROUTES[i.route] &&
        !ignoredRoutes(i.route) &&
        i.route !== `${pathname}${search}`,
    );
    if (index > -1) {
      return {
        historyIndex: index,
        historyBackRoute: routingHistory[index].route,
      };
    }
    return {};
  }, [ignoredRoutes, pathname, routingHistory, search]);

  const actualBackRoute = historyBackRoute || backRoute;

  const backRouteName = useMemo(
    () => (actualBackRoute ? getRouteName(actualBackRoute) : ''),
    [actualBackRoute],
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    contentRef,
    documentTitle: printProps?.label,
  });

  return (
    <StyledU21Page
      ref={contentRef}
      action={
        printProps ? (
          <U21Spacer horizontal>
            {action}
            <StyledU21Button
              color="primary"
              onClick={reactToPrintFn}
              startIcon={<IconDownload />}
              variant="contained"
            >
              Download
            </StyledU21Button>
          </U21Spacer>
        ) : (
          action
        )
      }
      beforeTitleContent={
        <>
          <StyledU21Button
            // eslint-disable-next-line react/no-children-prop
            children={backRouteName ? `Back to ${backRouteName}` : 'Back'}
            disableTitleCase
            startIcon={<IconArrowLeft />}
            // go back in history if found, otherwise go to the default back route
            onClick={() => (historyIndex ? go(-historyIndex) : push(backRoute))}
            {...backButtonProps}
          />
          {beforeTitleContent}
        </>
      }
      stickyHeader
      {...rest}
    >
      {Boolean(printProps) && (
        <StyledU21Spacer horizontal>
          <StyledUnit21Logo />
          <U21Spacer align="end" spacing={0}>
            <U21Typography variant="subtitle1">{orgName}</U21Typography>
            <U21Typography variant="subtitle1">
              {printProps?.label}
            </U21Typography>
            <U21Typography variant="subtitle1">
              {formatDatetime(new Date(), undefined, { showTimezone: true })}
            </U21Typography>
          </U21Spacer>
        </StyledU21Spacer>
      )}
      {children}
    </StyledU21Page>
  );
};

const StyledU21Page = styled(U21Page)`
  // override overflow: auto style when printing
  @media print {
    overflow: visible;
  }
`;

const StyledU21Button = styled(U21Button)`
  @media print {
    display: none;
  }
`;

const StyledU21Spacer = styled(U21Spacer)`
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StyledUnit21Logo = styled(Unit21Logo)`
  width: 250px;
`;
